import endpoints from './endpoints.json';

const baseUri = process.env.REACT_APP_WEB_API_URL || `${window.location.origin}/qestapi/v1`;

const httpActions = ['delete', 'get', 'patch', 'post', 'put'];

const buildUri = (uri, params) =>
  params
    ? `${baseUri}${Object.entries(params).reduce((workingUri, [key, value]) => {
        if (typeof value === 'undefined' || value === null) {
          return workingUri;
        } else if (workingUri.includes(`{${key}}`)) {
          // Replace in-url paramaters eg /mdd-tests/{worksheetId}/data
          return workingUri.replace(`{${key}}`, value);
        } else {
          // Else inject query params eg /mdd-tests?search={searchStr}
          if (!workingUri.includes('?')) workingUri += '?';
          else if (workingUri.slice(-1) !== '&') workingUri += '&';

          return `${workingUri}${getQueryFilterExpression(key, value)}`;
        }
      }, uri)}`
    : `${baseUri}${uri}`;

const getQueryFilterExpression = (key, value) => {
  // Query params values can be of several types:
  // * An array of values where each value applies to the same key
  // * A parameterless function to evaluate the value (e.g. for date based expressions we want to keep current)
  // * An object of structure { operator, value }
  // * A raw value (operator is assumed to be =)

  // Note these values can be compounded (i.e. you can have an array of objects to specify different operators)
  //  so this gets called recursively to resolve those.

  if (value.constructor === Array) {
    return value.map((v) => getQueryFilterExpression(key, v)).join(`&`);
  } else if (value.constructor === Function && value.length === 0) {
    return getQueryFilterExpression(key, value());
  } else if (value.constructor === Object && value.operator) {
    return `${key}${value.operator}${value.value}`;
  } else {
    return `${key}=${value}`;
  }
};

const handleEndpoint = (path) => ({
  // Not HTTP get, Proxy get
  get: (_target, prop) => {
    if (httpActions.includes(prop) || prop === 'buildUri') {
      return handleAction(prop, path);
    } else {
      return new Proxy({}, handleEndpoint(path === undefined ? prop : path + '.' + prop));
    }
  },
});

const handleAction = (action, path) => {
  const uri = endpoints[path];
  if (!uri) {
    throw new Error(`Invalid endpoint accessed. Path:"${path}"`);
  }
  return createActionHandler(uri)[action];
};

// Makes all network errors look the same as QApiException errors (from the Web API class), so we can handle them
// consistently.
const fetchAndHandleError = (...args) =>
  fetch(...args).then((res) => {
    if (res.ok) {
      return res.json();
    }

    var contentType = res.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
      return res
        .clone() // clone the response it the body turns out not to be a QApiException, we can
        .json() // still return the response body as plain text
        .then((err) => {
          if (err.Success === false && err.Error && err.Error.Code && err.Error.Message) {
            //looks like a QApiException
            return err;
          } else {
            //not a QApiException
            //make it look like a QApiException with the response body as the 'stack trace'
            return res.text().then((text) => {
              return {
                Success: false,
                Error: {
                  Code: res.status,
                  Message: res.statusText,
                  StackTraceString: text,
                },
              };
            });
          }
        });
    }

    //not a JSON body
    //make it look like a QApiException with the response body as the 'stack trace'
    return res.text().then((text) => {
      return {
        Success: false,
        Error: {
          Code: res.status,
          Message: res.statusText,
          StackTraceString: text,
        },
      };
    });
  });

const defaultHeaders = () => {
  return {
    // This user agent string is used by QESTNet to identify actions from QEST Web App (see QESTLab.Core.QestApplication). Revise with care.
    QESTUserAgent: `QEST Web App/${process.env.REACT_APP_VERSION} [${
      (!!window?.qest?.userAgent && window.qest.userAgent) || navigator.userAgent
    }]`,
  };
};

const createActionHandler = (uri) => ({
  delete: (id) =>
    fetchAndHandleError(buildUri(uri, id), {
      credentials: 'include',
      method: 'DELETE',
      headers: {
        ...defaultHeaders(),
      },
    }),
  get: (params) =>
    fetchAndHandleError(buildUri(uri, params), {
      credentials: 'include',
      method: 'GET',
      headers: {
        ...defaultHeaders(),
      },
    }),
  patch: (data, params) =>
    fetchAndHandleError(buildUri(uri, params), {
      body: JSON.stringify(data),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        ...defaultHeaders(),
      },
      method: 'PATCH',
    }),
  post: (data, params) =>
    fetchAndHandleError(buildUri(uri, params), {
      body: JSON.stringify(data),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        ...defaultHeaders(),
      },
      method: 'POST',
    }),
  put: (data, params) =>
    fetchAndHandleError(buildUri(uri, params), {
      body: JSON.stringify(data),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        ...defaultHeaders(),
      },
      method: 'PUT',
    }),
  buildUri: (params) => buildUri(uri, params),
});

export default new Proxy({}, handleEndpoint());
